import './firebase/config';  // Firebaseの初期化を最初に行う
import { Routes, Route, useLocation } from 'react-router-dom';  // react-router-domから明示的にインポート
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import PrivateRoute from './components/auth/PrivateRoute';
import { VerifiedRoute } from './components/auth/VerifiedRoute';
import { Box, Container } from '@mui/material';
import Navbar from './components/layout/Navbar';
import BottomNav from './components/layout/BottomNav';
import DesktopFooter from './components/layout/DesktopFooter';
import PageWrapper from './components/layout/PageWrapper';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme'; // 作成したテーマをインポート
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale'; // 日本語ロケールを使用する場合
import { useEffect } from 'react';
import { pageview } from './utils/analytics';
import { setNotificationHandler } from './services/api'; // APIに通知ハンドラーを設定するための関数をインポート

// ペインページ
import Home from './pages/Home';
import RoomSearch from './pages/RoomSearch';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import ProfileSetup from './pages/ProfileSetup';
import Settings from './pages/Settings';
import CreateListing from './pages/CreateListing';
import RoomDetail from './pages/RoomDetail';
import UserDetail from './pages/UserDetail';
import ChatRoom from './pages/ChatRoom';
import MyRooms from './pages/MyRooms';
import EditRoom from './pages/EditRoom';
import Favorites from './pages/Favorites';

// 認証ページ
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import EmailVerification from './pages/auth/EmailVerification';

// ヘルプ＆サポートページ
import Contact from './pages/Contact';
import HelpCenter from './pages/HelpCenter';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// ガイドページ
import Guide from './pages/Guide';
import GettingStarted from './pages/guide/GettingStarted';
import HowToPost from './pages/guide/HowToPost';
import SafeRoomShare from './pages/guide/SafeRoomShare';

// ブログページ
import BlogHome from './pages/blog/BlogHome';
import BlogPost from './pages/blog/BlogPost';

// レター検索ページ
import RenterSearch from './pages/RenterSearch';

// App内部コンポーネントを作成して、NotificationContext内でAPIハンドラーを設定
function AppContent() {
    const location = useLocation();
    const isChatRoom = location.pathname.match(/^\/messages\/[^/]+$/);
    const { showNotification } = useNotification(); // NotificationContextからshowNotificationを取得

    // ページビューのトラッキング
    useEffect(() => {
        pageview(location.pathname + location.search);
    }, [location]);

    // API通知ハンドラーの設定
    useEffect(() => {
        setNotificationHandler(showNotification);
    }, [showNotification]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {!isChatRoom && <Navbar />}
            <Container
                component="main"
                sx={{
                    flex: 1,
                    py: 2,
                    mb: { xs: 7, md: 0 },
                    px: { xs: 1, sm: 2, md: 3 }, // モバイルでは余白を小さく
                    maxWidth: { xs: '100%', sm: '600px', md: '960px', lg: '1280px' }
                }}
            >
                <Routes>
                    {/* メインページ */}
                    <Route path="/" element={<Home />} />
                    <Route path="/room-search" element={<RoomSearch />} />
                    <Route path="/renters" element={<RenterSearch />} />
                    <Route path="/messages" element={
                        <VerifiedRoute>
                            <Messages />
                        </VerifiedRoute>
                    } />
                    <Route path="/messages/:chatRoomId" element={
                        <VerifiedRoute>
                            <ChatRoom />
                        </VerifiedRoute>
                    } />
                    <Route path="/profile" element={
                        <VerifiedRoute>
                            <Profile />
                        </VerifiedRoute>
                    } />
                    <Route path="/profile-setup" element={<ProfileSetup />} />
                    <Route path="/settings" element={
                        <VerifiedRoute>
                            <Settings />
                        </VerifiedRoute>
                    } />
                    <Route path="/create-listing" element={
                        <VerifiedRoute>
                            <CreateListing />
                        </VerifiedRoute>
                    } />
                    <Route path="/rooms/:id" element={<RoomDetail />} />
                    <Route path="/users/:id" element={<UserDetail />} />
                    <Route path="/my-rooms" element={
                        <VerifiedRoute>
                            <MyRooms />
                        </VerifiedRoute>
                    } />
                    <Route path="/rooms/edit/:id" element={
                        <VerifiedRoute>
                            <EditRoom />
                        </VerifiedRoute>
                    } />
                    <Route path="/favorites" element={
                        <VerifiedRoute>
                            <Favorites />
                        </VerifiedRoute>
                    } />


                    {/* 認証ページ */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/email-verification" element={<EmailVerification />} />

                    {/* ヘルプ＆サポートページ */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />

                    {/* ガイドページ */}
                    <Route path="/guide" element={<Guide />} />
                    <Route path="/guide/getting-started" element={<GettingStarted />} />
                    <Route path="/guide/how-to-post" element={<HowToPost />} />
                    <Route path="/guide/safe-roomshare" element={<SafeRoomShare />} />

                    {/* ブログページ */}
                    <Route path="/blog" element={<BlogHome />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                </Routes>
            </Container>
            {!isChatRoom && <BottomNav />}
            {!isChatRoom && <DesktopFooter />}
        </Box>
    );
}

function App() {
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={ja}>
            <ThemeProvider theme={theme}>
                <NotificationProvider>
                    <AuthProvider>
                        <PageWrapper>
                            <AppContent />
                        </PageWrapper>
                    </AuthProvider>
                </NotificationProvider>
            </ThemeProvider>
        </LocalizationProvider>
    );
}

export default App; 